import { useContext, useState, createContext, useCallback, useRef } from "react"
import { node, oneOfType, arrayOf } from "prop-types"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import Jolt, { ChannelSubscription } from "@planningcenter/jolt-client"
import { useSession } from "source/shared/hooks/useSession"
import { WebBootContext } from "source/publishing/WebBoot"
import { useJoltChannel } from "../hooks/useJolt"

type JoltSubscription = InstanceType<typeof ChannelSubscription>

type SubscribeOptions = {
  fetchSubscribeTokenFn: () => Promise<string>
}
type JoltContextType = {
  joltConnectionId: string | undefined
  requestJoltInstance: () => void
  subscribe: (
    channel: string,
    args: SubscribeOptions,
  ) => JoltSubscription | undefined
  unsubscribe: (channel: string) => void
}
const defaultJoltContext = {
  joltConnectionId: undefined,
  requestJoltInstance: () => {},
  subscribe: () => undefined,
  unsubscribe: () => {},
}

export const JoltContext = createContext<JoltContextType>(defaultJoltContext)
const fetchAuthTokenFn = async () => {
  const response = await sessionApiClient.post("/global/v2/jolt_connect")
  return response.data.id
}

const fetchNotificationsSubscribeTokenFn = async (
  channel: string,
  connection_id: string,
) => {
  const response = await sessionApiClient.post(
    `/notifications/v2/me/jolt_subscribe`,
    {
      data: {
        attributes: {
          channel,
          connection_id,
        },
      },
    },
  )
  return response.data.id
}

const fetchChatSubscribeTokenFn = async (
  channel: string,
  connection_id: string,
) => {
  const response = await sessionApiClient.post(`/chat/v2/me/jolt_subscribe`, {
    data: {
      attributes: {
        channel,
        cid: connection_id,
      },
    },
  })
  return response.data.id
}

class JoltProxyThatHandlesRepeatedChannelSubscriptions {
  private jolt: Jolt
  private subscriptions: Record<
    string,
    { subscribers: number; subscription: JoltSubscription }
  >

  constructor(jolt: Jolt) {
    this.jolt = jolt
    this.subscriptions = {}
  }

  get connection() {
    return this.jolt.connection
  }

  get connectionId() {
    return this.jolt.connection.connectionId
  }

  disconnect() {
    this.jolt.disconnect()
  }

  subscribe(channel: string, args: SubscribeOptions) {
    if (!this.subscriptions[channel]) {
      this.subscriptions[channel] = {
        subscription: this.jolt.subscribe(channel, args),
        subscribers: 0,
      }
    }

    this.subscriptions[channel].subscribers += 1
    return this.subscriptions[channel].subscription
  }

  unsubscribe(channel: string) {
    if (!this.subscriptions[channel]) {
      return
    }

    this.subscriptions[channel].subscribers -= 1

    if (this.subscriptions[channel].subscribers === 0) {
      this.jolt.unsubscribe(channel)
      delete this.subscriptions[channel]
    }
  }
}

function buildNextJolt(
  joltWebsocketUrl: string,
  fetchAuthTokenFn: () => Promise<string>,
) {
  if (!joltWebsocketUrl) return null

  return new JoltProxyThatHandlesRepeatedChannelSubscriptions(
    new Jolt(
      joltWebsocketUrl,
      { fetchAuthTokenFn },
      __DEV__ ? {} : { logToConsole: true },
    ),
  )
}

export function useNotificationsChannel() {
  const { data: currentPerson } = useSession(false)
  const channelName = currentPerson?.id
    ? `church_center.notifications.people.${currentPerson.id}`
    : null

  return useJoltChannel(fetchNotificationsSubscribeTokenFn, channelName)
}

export function useChatChannel() {
  const { data: currentPerson } = useSession(false)
  const channelName = currentPerson?.id
    ? `chat.people.${currentPerson.id}`
    : null

  return useJoltChannel(fetchChatSubscribeTokenFn, channelName)
}

export const JoltProvider = ({ children }: { children: React.ReactNode }) => {
  const joltRef =
    useRef<JoltProxyThatHandlesRepeatedChannelSubscriptions | null>(null)
  const [joltConnectionId, setJoltConnectionId] = useState<string | undefined>()
  const { joltWebsocketUrl } = useContext(WebBootContext)

  const createJoltInstance = useCallback(() => {
    if (joltRef.current) return

    const newJolt = buildNextJolt(joltWebsocketUrl, fetchAuthTokenFn)
    if (newJolt) {
      joltRef.current = newJolt
      joltRef.current.connectionId.then((id) => setJoltConnectionId(id))
    }
  }, [joltWebsocketUrl])

  const subscribe = useCallback(
    (
      channel: string,
      args: SubscribeOptions,
    ): JoltSubscription | undefined => {
      if (!channel) return
      createJoltInstance()
      return joltRef.current?.subscribe(channel, args)
    },
    [createJoltInstance],
  )

  const unsubscribe = useCallback((channel: string) => {
    if (channel) joltRef.current?.unsubscribe(channel)
  }, [])

  return (
    <JoltContext.Provider
      value={{
        joltConnectionId,
        requestJoltInstance: createJoltInstance,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </JoltContext.Provider>
  )
}

JoltProvider.propTypes = {
  children: oneOfType([arrayOf(node), node]),
}
